import { useState, useRef } from 'react';
import { supabase } from '../../../lib/supabase';

interface OrderFormProps {
  userEmail: string;
  userName: string;
  onClose: () => void;
  onSuccess: () => void;
}

export default function OrderForm({ userEmail, userName, onClose, onSuccess }: OrderFormProps) {
  const [formData, setFormData] = useState({
    orderTitle: '',
    orderDescription: '',
  });
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      // Yeni dosyaları mevcut dosyalara ekle
      const newFiles = Array.from(e.target.files);
      setFiles(prevFiles => [...prevFiles, ...newFiles]);
      
      // Input'u temizle ki aynı dosya tekrar seçilebilsin
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const uploadFiles = async () => {
    const uploadedUrls: string[] = [];

    for (const file of files) {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `${userEmail}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('order-files')
        .upload(filePath, file);

      if (uploadError) {
        throw uploadError;
      }

      const {
        data: { publicUrl },
      } = supabase.storage.from('order-files').getPublicUrl(filePath);

      uploadedUrls.push(publicUrl);
    }

    return uploadedUrls;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setUploading(true);

    try {
      let fileUrls: string[] = [];

      if (files.length > 0) {
        fileUrls = await uploadFiles();
      }

      // Önce siparişi oluştur
      const { data: orderData, error: insertError } = await supabase
        .from('orders')
        .insert({
          customer_email: userEmail,
          customer_name: userName,
          order_title: formData.orderTitle,
          order_description: formData.orderDescription,
          file_urls: fileUrls,
          status: 'pending',
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // Siparişi sepete ekle
      if (orderData) {
        const { error: cartError } = await supabase.from('cart').insert({
          customer_email: userEmail,
          order_id: orderData.id,
          order_title: formData.orderTitle,
          order_description: formData.orderDescription,
          file_urls: fileUrls,
        });

        if (cartError) {
          console.error('Sepete eklenirken hata:', cartError);
          // Sepete ekleme hatası olsa bile sipariş oluşturuldu, devam et
        }
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Sipariş oluşturulurken bir hata oluştu');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl lg:rounded-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 lg:px-6 py-3 lg:py-4 flex items-center justify-between rounded-t-xl lg:rounded-t-2xl">
          <h3 className="text-lg lg:text-xl font-semibold text-gray-900">Yeni Sipariş Oluştur</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-all cursor-pointer"
          >
            <i className="ri-close-line text-xl lg:text-2xl text-gray-500"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 lg:p-6 space-y-4 lg:space-y-5 bg-white">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5 lg:mb-2">
              Sipariş Başlığı <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <i className="ri-file-text-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                value={formData.orderTitle}
                onChange={(e) => setFormData({ ...formData, orderTitle: e.target.value })}
                className="w-full bg-gray-50 border border-gray-300 rounded-lg pl-10 pr-4 py-2.5 lg:py-3 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none text-sm"
                placeholder="Örn: Logo Tasarımı"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5 lg:mb-2">Açıklama</label>
            <textarea
              value={formData.orderDescription}
              onChange={(e) => setFormData({ ...formData, orderDescription: e.target.value })}
              rows={4}
              maxLength={500}
              className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-2.5 lg:py-3 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none text-sm resize-none"
              placeholder="Siparişiniz hakkında detaylı bilgi verin..."
            />
            <p className="text-gray-500 text-xs mt-1 text-right">{formData.orderDescription.length}/500</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5 lg:mb-2">
              Dosya Yükle (Opsiyonel)
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 lg:p-6 text-center hover:border-green-400 transition-colors bg-gray-50">
              <input
                type="file"
                multiple
                onChange={handleFileChange}
                className="hidden"
                id="file-upload"
                ref={fileInputRef}
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer flex flex-col items-center gap-2"
              >
                <div className="w-10 h-10 lg:w-12 lg:h-12 bg-white rounded-lg flex items-center justify-center border border-gray-200">
                  <i className="ri-upload-cloud-2-line text-xl lg:text-2xl text-green-500"></i>
                </div>
                <p className="text-sm text-gray-600">
                  Dosya seçmek için tıklayın
                </p>
                <p className="text-xs text-gray-500">Birden fazla dosya seçebilirsiniz</p>
              </label>
            </div>

            {files.length > 0 && (
              <div className="mt-3 lg:mt-4 space-y-2">
                {files.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-2.5 lg:p-3 bg-gray-50 border border-gray-200 rounded-lg"
                  >
                    <div className="w-8 h-8 bg-white rounded flex items-center justify-center border border-gray-200 flex-shrink-0">
                      <i className="ri-file-line text-lg text-green-500"></i>
                    </div>
                    <span className="text-sm text-gray-900 flex-1 truncate">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => setFiles(files.filter((_, i) => i !== index))}
                      className="p-1.5 hover:bg-gray-200 rounded cursor-pointer flex-shrink-0"
                    >
                      <i className="ri-close-line text-gray-500"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 lg:px-4 py-2.5 lg:py-3 rounded-lg text-sm flex items-center gap-2">
              <i className="ri-error-warning-line flex-shrink-0"></i>
              <span>{error}</span>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 lg:px-6 py-2.5 lg:py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-all whitespace-nowrap cursor-pointer text-sm lg:text-base"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={uploading}
              className="flex-1 px-4 lg:px-6 py-2.5 lg:py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg font-medium hover:from-green-600 hover:to-green-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer text-sm lg:text-base"
            >
              {uploading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  Oluşturuluyor...
                </span>
              ) : (
                'Kaydet ve Başlat'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
